import {
  normalizeApostrophes,
  removeExtraSpaces,
  removePunctuations,
} from 'src/shared/utilities/string';

export type TypedAnswerRules = {
  ignorePunctuation: boolean;
  ignoreCapitalization: boolean;
  ignoreWordOrder: boolean;
};

/**
 * Normalizes a typed / dictated answer according to the drill's typing rules
 * so it can be compared with the expected answer. Whitespace is always
 * collapsed and trimmed — voice-to-text and punctuation removal both leave
 * stray spaces behind. Apostrophe look-alikes (iPhone ’, Ukrainian ʼ, …) are
 * always folded to ' so contractions match on every device. With `ignoreWordOrder` the words are sorted so that
 * two answers made of the same words compare equal.
 */
export function normalizeTypedAnswer(
  value: string,
  rules: TypedAnswerRules,
): string {
  let result = normalizeApostrophes(value);
  if (rules.ignorePunctuation) result = removePunctuations(result);
  result = removeExtraSpaces(result);
  if (rules.ignoreCapitalization) result = result.toLocaleLowerCase();
  if (rules.ignoreWordOrder) result = result.split(' ').sort().join(' ');
  return result;
}

/**
 * Checks a typed / dictated answer against one or more accepted answers,
 * applying the same normalization to both sides.
 */
export function isTypedAnswerCorrect(
  answer: string,
  correctAnswers: string | string[],
  rules: TypedAnswerRules,
): boolean {
  const accepted = Array.isArray(correctAnswers)
    ? correctAnswers
    : [correctAnswers];
  const normalizedAnswer = normalizeTypedAnswer(answer, rules);
  return accepted.some(
    (item) => normalizeTypedAnswer(item, rules) === normalizedAnswer,
  );
}
